/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import * as Accordion from '@radix-ui/react-accordion'
import { ArrowUpFromDot } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React from 'react'

import { MtaKitStoryblokWithSlug } from '@/components/e-commerce/kits/kit-accordion/KitAccordionOverview'
import { KitCard } from '@/components/e-commerce/kits/kit-accordion/KitCard'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type KitAccordionCategoryItemProps = {
  title: string
  category: string
  description: string | React.ReactNode
  bgColor: string
  kits: Array<MtaKitStoryblokWithSlug>
}

/**
 * An item within the kit accordion, representing a category of kits.
 */
const KitAccordionCategoryItem: React.FC<KitAccordionCategoryItemProps> = ({
  category,
  title,
  description,
  bgColor,
  kits,
}) => {
  const t = useTranslations('KitAccordionCategoryItem')

  return (
    <Accordion.Item value={category}>
      <Accordion.Header className='flex'>
        <Accordion.Trigger
          className={cn(
            'group flex flex-1 cursor-pointer items-center justify-between p-5 text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1',
            'text-2xl font-bold text-white',
            'focus-visible:ring-white/75',
            'transition-colors motion-reduce:transition-none hover:brightness-95 focus-visible:brightness-95',
          )}
          style={{
            backgroundColor: bgColor,
          }}
        >
          <span>{title}</span>
          <ArrowUpFromDot
            className='border border-white rounded-full p-1.5 h-8 w-8 text-white transition-transform duration-300 motion-reduce:transition-none rotate-180 group-data-[state=open]:rotate-0'
            aria-hidden='true'
          />
        </Accordion.Trigger>
      </Accordion.Header>

      <Accordion.Content
        className={cn(
          'overflow-hidden bg-white text-sm transition-[height] motion-reduce:transition-none motion-reduce:animate-none data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        )}
      >
        <div className='p-4 md:p-6'>
          {description && (
            <div className='mb-4 text-base text-gray-700'>
              {typeof description === 'string' ? <p>{description}</p> : description}
            </div>
          )}
          <div className={cn('flex flex-wrap gap-4')}>
            {/* Storyblok root content uids are only unique within a story, so duplicated stories share a _uid. */}
            {kits.map((kit, index) => (
              <KitCard key={`${kit._uid}-${index}`} kit={kit} />
            ))}
            {kits.length === 0 && <p className='text-gray-500 italic'>{t('noKitsFound')}</p>}
          </div>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  )
}

export { KitAccordionCategoryItem }
